import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";

import { SPONSOR_POST_EVENT_REPORT_RELEASED } from "src/shared/constants/featureFlags";
import { Route } from "src/shared/constants/route";
import { useSponsorContext } from "src/shared/contexts/SponsorContext";

import { useGetSponsorPostEventReportQuery } from "./graphql/getSponsorPostEventReport.generated";
import { REPORT_YEAR, shouldShowPostEventReportCard } from "./reportAccess";

const PostEventReportCard: React.FC = () => {
  const navigate = useNavigate();
  const { company, loading: sponsorLoading } = useSponsorContext();

  // Same operation and variables as the report page, so Apollo serves both
  // from one cache entry rather than fetching twice.
  const { data } = useGetSponsorPostEventReportQuery({
    variables: { sponsor_company_slug: company?.slug },
    skip: !SPONSOR_POST_EVENT_REPORT_RELEASED || sponsorLoading,
  });
  const report = data?.sponsorPostEventReport ?? undefined;

  if (
    !shouldShowPostEventReportCard(SPONSOR_POST_EVENT_REPORT_RELEASED, report)
  )
    return null;

  return (
    <CardContainer>
      <IconWell aria-hidden="true">
        <DocumentTextIcon />
      </IconWell>
      <CardCopy>
        <Eyebrow>Your {REPORT_YEAR} recap</Eyebrow>
        <CardTitle>Your post-event report is ready.</CardTitle>
        <CardDescription>
          See the reach, engagement, and impact your sponsorship made possible.
        </CardDescription>
      </CardCopy>
      <Button
        color="primary"
        size="md"
        onClick={() => navigate(Route.SPONSOR_POST_EVENT_REPORT)}
      >
        View report
      </Button>
    </CardContainer>
  );
};

const CardContainer = styled.section`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  margin-top: 32px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 14px;
  background: linear-gradient(135deg, #f5fcff 0%, #ffffff 75%);
  box-shadow: 0 8px 24px rgba(14, 52, 96, 0.07);

  ${({ theme }) => theme.mediaQueries.largeMobile`
    grid-template-columns: auto minmax(0, 1fr);
    gap: 16px;
    padding: 20px;

    > button {
      grid-column: 1 / -1;
      width: 100%;
    }
  `}
`;

const IconWell = styled.div`
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 12px;
  color: ${({ theme }) => theme.globalConstants.color.navySecondary};
  background: ${({ theme }) => theme.globalConstants.color.bluePrimary3Light};

  svg {
    width: 25px;
    height: 25px;
  }
`;

const CardCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.p`
  margin: 0 0 4px;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 21px;
  line-height: 1.25;
`;

const CardDescription = styled.p`
  margin: 6px 0 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  line-height: 1.5;
`;

export default PostEventReportCard;
